import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { DateTime } from 'luxon';
import type { IExpectedVsActualBucket, ProductivityGranularity } from '@interfaces/IProductivity';
import styles from './ExpectedVsActualChart.module.css';
import { ChartTooltip } from './ChartTooltip/ChartTooltip';
import { ChartTick } from './ChartTick/ChartTick';

const CHART_MARGIN = { top: 8, right: 8, left: 8, bottom: 0 } as const;
const TOOLTIP_CURSOR = { fill: 'var(--blue-tint)' } as const;
const BAR_RADIUS: [number, number, number, number] = [3, 3, 0, 0];

interface IExpectedVsActualChartProps {
  buckets: IExpectedVsActualBucket[];
  granularity: ProductivityGranularity;
}

/** Bucket-start ISO → short axis label, formatted per granularity in the
 *  active locale: day / week → "MMM d" (the date / week start), month → month
 *  name. recharts thins the rendered ticks so a full month of daily bars
 *  doesn't crowd the axis. */
function axisLabel(startIso: string, granularity: ProductivityGranularity, locale: string): string {
  const dt = DateTime.fromISO(startIso, { zone: 'utc' }).setLocale(locale);
  switch (granularity) {
    case 'day':
    case 'week':
      return dt.toFormat('LLL d');
    case 'month':
      return dt.toFormat('LLL');
  }
}

export function ExpectedVsActualChart({ buckets, granularity }: IExpectedVsActualChartProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const rows = useMemo(
    () =>
      buckets.map((b) => ({
        label: axisLabel(b.startIso, granularity, locale),
        startIso: b.startIso,
        actual: b.actualWorkDays,
        earned: b.earnedWorkDays,
        productivity: b.productivity,
      })),
    [buckets, granularity, locale],
  );

  return (
    <div className={styles.chart}>
      <ResponsiveContainer width="100%" height="100%" minHeight={140}>
        <BarChart data={rows} margin={CHART_MARGIN} barGap={2} accessibilityLayer={false}>
          <XAxis
            dataKey="label"
            tick={<ChartTick rows={rows} />}
            height={34}
            axisLine={{ stroke: 'var(--g200)' }}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <Tooltip cursor={TOOLTIP_CURSOR} content={<ChartTooltip locale={locale} />} />
          <Bar
            dataKey="actual"
            name={t('productivity.expected.legendActual')}
            fill="var(--prod-bar-actual)"
            radius={BAR_RADIUS}
          />
          <Bar
            dataKey="earned"
            name={t('productivity.expected.legendExpected')}
            fill="var(--prod-bar-earned)"
            radius={BAR_RADIUS}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}